import { observer } from "mobx-react-lite";
import { TextSort } from "../../helpers/SorterHelper";
import { DownOutlined, UpOutlined, EyeOutlined, DeleteOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useStore } from "../../stores/root-store-context";
import { useEffect, useState } from "react";
import { getUserComponent } from "../../helpers/user";
import { useTranslation } from "react-i18next";
import { Button, Flex, Modal, Tooltip, Popconfirm, Spin, Table, TableProps, Tag, Pagination, App, Tabs } from "antd";
import { TestAttempt, UserTestAssignment } from "../../stores/TestsStore";
import { useNavigate } from "react-router-dom";
import dayjs from 'dayjs';
import { toJS } from "mobx";
import { SkillLevelName } from "../../helpers/skills";
import { ColumnType } from "antd/es/table";
import { Roles } from "../../enums/Roles";
import { Skill } from "../../models/Skill";
import { Employee } from "../../models/Employee";
import { AssignmentStatus } from "../../enums/AssignmentStatus";

const { TabPane } = Tabs;

export const UserTestInfo = observer(({ userAssignments, companyAssignments, setUserAssignments, setCompanyAssignments, loading, setLoading }: {
    userAssignments: UserTestAssignment[],
    companyAssignments: UserTestAssignment[],
    loading: boolean,
    setLoading: (loading: boolean) => void,
    setUserAssignments: (userAssignments: UserTestAssignment[]) => void,
    setCompanyAssignments: (userAssignments: UserTestAssignment[]) => void,
}) => {
    const { t } = useTranslation();
    const { testsStore } = useStore();
    const { applicationStore, employeesStore } = useStore();
    const [expandedSkills, setExpandedSkills] = useState<{ [key: string]: boolean }>({});
    const [filteredUser, setFilteredUser] = useState<Employee[]>([]);
    const [expandedRowKey, setExpandedRowKey] = useState<string[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedAttempts, setSelectedAttempts] = useState<TestAttempt[]>([]);
    const [allUserAttempt, setAllUserAttempt] = useState<TestAttempt[]>([]);
    const { message } = App.useApp();
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [currentPageAttempt, setCurrentPageAttempt] = useState<number>(1);
    const [pageSizeAttemptTable, setPageSizeAttemptTable] = useState<number>(5);

    useEffect(() => {
        setLoading(true);

        employeesStore.getUsers().then(res => {
            setFilteredUser(employeesStore.users.filter(u => u.id == applicationStore.user.id));
        });

        const fetchData = async () => {

            if (!testsStore.isInitialized) {
                await testsStore.initializeStore();
            }

            var allUserAttempt = await testsStore.getAllTestAttemptsByUserId(applicationStore.user.id);
            setAllUserAttempt(allUserAttempt);
        }

        fetchData();
        setLoading(false);
    }, []);

    const handleChangePageAttempt = (page: number, pageSize: number) => {
        setCurrentPageAttempt(page);
        setPageSizeAttemptTable(pageSize);
    };

    const handleChangePage = (page: number, pageSize?: number) => {
        setCurrentPage(page);
        if (pageSize) {
            setPageSize(pageSize);
        }
    };

    const showAttempts = (attempts: TestAttempt[]) => {
        setSelectedAttempts(attempts);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setSelectedAttempts([]);
    };

    const onExpandHandle = (expanded: boolean, record: Employee) => {
        if (expanded) {
            setExpandedRowKey([record.id]);
        } else {
            setExpandedRowKey([]);
        }
    };

    const handleDeleteAssignment = async (assignment: UserTestAssignment) => {
        if (assignment.assignmentStatusId == AssignmentStatus.Started) {
            message.error(t("tests.You can not delete assignment"));
            return;
        }

        const result = await testsStore.deleteAssignmentById(assignment.id.toString());

        if (result) {
            message.success(t("Success"));

            const newAssignments = userAssignments.filter(ua => ua.id !== assignment.id);

            setUserAssignments(newAssignments);

            const isCompanyAssignmentExists = companyAssignments.some(ua => ua.id === assignment.id);
            if (isCompanyAssignmentExists) {
                const updatedCompanyAssignments = companyAssignments.filter(ua => ua.id !== assignment.id);
                setCompanyAssignments(updatedCompanyAssignments);
            }
        } else {
            message.error(t("add_test.DeleteFailed"));
        }
    };

    const columnsAssignment = [
        {
            title: t("add_test.Name"),
            key: "titleTest",
            render: (record: UserTestAssignment) => {
                var existTest = testsStore.testsPublic.find(t => t.id == record.testId);

                if (!existTest) {
                    existTest = testsStore.testsCompany.find(t => t.id == record.testId);
                }

                return existTest ? `${applicationStore.skills.find(s => s.skillId == existTest?.skillId?.toString())?.title} - ${existTest.level}` : t("tests.no_test_name_find");
            }
        },
        {
            title: t("Description"),
            dataIndex: ["test", "description"],
            key: "description",
            render: (desc: string) => desc || t("(no description)")
        },
        ...(applicationStore?.siteConfig?.isShowLanguage
            ? [{
                title: t("tests.languagePass"),
                key: "language",
                render: (record: UserTestAssignment) => t(`languages.${record.language}`)
            } as ColumnType<UserTestAssignment>]
            : []),
        {
            title: t("tests.grade"),
            key: "grade",
            render: (_: any, record: UserTestAssignment) =>
                record.testAttempts.length > 0
                    ? `${Math.max(...record.testAttempts.map((t) => t.percentage))}/100%`
                    : "–"
        },
        {
            title: t("add_test.Level"),
            dataIndex: ["test", "level"],
            key: "level"
        },
        {
            title: t("add_test.NumberAttempts"),
            dataIndex: "numberOfAttempts",
            key: "numberOfAttempts"
        },
        {
            title: t("add_test.NumberQuestions"),
            dataIndex: ["test", "questionsToProvideCount"],
            key: "questionsToProvideCount"
        },
        {
            title: `${t("add_test.MaxTime")} (${t("passage_test.minutes")})`,
            dataIndex: ["test", "maxTimeMinutes"],
            key: "maxTime"
        },
        {
            title: t("tests.AssignedDate"),
            dataIndex: "assignedDate",
            key: "assignedDate",
            render: (date: string) =>
                date
                    ? new Date(date).toLocaleDateString("uk-UA", { day: "numeric", month: "numeric", year: "numeric" }).replace(/\//g, ".")
                    : t("No date")
        },
        {
            title: t("tests.selectDeadlineDate"),
            dataIndex: "finishAssignedDate",
            key: "finishAssignedDate",
            render: (text: string) => text ? dayjs(text).format("DD.MM.YYYY") : t("No date")
        },
        {
            title: t("Status"),
            dataIndex: ["assignmentStatus", "name"],
            key: "status",
            render: (name: string) => t(name)
        },
        {
            title: t("Actions"),
            key: "actions",
            render: (_: any, record: UserTestAssignment) => (
                <>
                    {(record.assignmentStatusId !== AssignmentStatus.Expired && record.assignmentStatusId !== AssignmentStatus.Completed) && (
                        <Button
                            type="text"
                            shape="circle"
                            icon={<PlayCircleOutlined />}
                            onClick={() => navigate(`/${applicationStore.company.Tenant}/tests/passage/${record.testId}`)}
                            title={t("Start Test")}
                        />
                    )}
                    <Button
                        type="text"
                        shape="circle"
                        icon={<EyeOutlined />}
                        onClick={() => showAttempts(allUserAttempt.filter(at => (at.userTestAssignmentId != null && at.userTestAssignmentId == record.id)))}
                        disabled={allUserAttempt.filter(at => (at.userTestAssignmentId != null && at.userTestAssignmentId == record.id)).length === 0}
                        title={t("View Attempts")}
                    />
                    {applicationStore.user?.roles?.some((r: any) => r.id === 1) && (
                        <Popconfirm
                            title={t("tests.Delete the assignment?")}
                            okText={t("Yes")}
                            cancelText={t("No")}
                            onConfirm={async () => await handleDeleteAssignment(record)}
                        >
                            <Button danger type="text" shape="circle" icon={<DeleteOutlined />} />
                        </Popconfirm>
                    )}
                </>
            )
        }
    ];

    const columns: TableProps<Employee>['columns'] = [
        {
            title: `${t("user.Name")}`,
            dataIndex: 'photoId',
            key: 'photo',
            render: (data, record) => <div>{getUserComponent(applicationStore.siteConfig.baseCloudinary, record, applicationStore.company?.Tenant)}</div>,
        },
        Table.EXPAND_COLUMN,
        {
            title: `${t("user.JobTitle")}`,
            dataIndex: 'jobTitleId',
            key: 'jobTitle',
            render: (jobTitleId) => {
                const jobTitle = employeesStore.jobTitles.find(el => el.id === jobTitleId)?.title;
                return <div>{jobTitle || t(" ")}</div>;
            },
        },
        {
            title: `${t("Skills")}`,
            dataIndex: 'userSkills',
            key: 'userSkills',
            render: (skills, project) => <Flex wrap style={{ maxWidth: 200 }} gap={10}>
                {(expandedSkills[project.id] ? skills : skills.slice(0, 5)).map((el: any) => (
                    <Tooltip key={el.skill.title} title={`${t("tests.skill_level")}: ${t(SkillLevelName[el.skillLevel])}`}>
                        <Tag color={el.isConfirmed ? "green" : "blue"} key={el.skill.title}>{el.skill.title}</Tag>
                    </Tooltip>
                ))}
                {skills?.length > 5 && (
                    expandedSkills[project.id] ? (
                        <Tag onClick={() => setExpandedSkills(prevState => ({
                            ...prevState,
                            [project.id]: false
                        }))} style={{ cursor: 'pointer' }} color="blue">{t("ShowLess")}</Tag>
                    ) : (
                        <Tag onClick={() => setExpandedSkills(prevState => ({
                            ...prevState,
                            [project.id]: true
                        }))} style={{ cursor: 'pointer' }} color="blue">+{skills.length - 5} more</Tag>
                    )
                )}
            </Flex>,
        },
        {
            title: `${t("tests.assignedTests")}`,
            render: (record, project) => {
                const assignedTests = userAssignments.filter(
                    (ua) => ua.assignedToUserId === Number(record.id)
                );
                const relevantSkills = assignedTests
                    .map((assignment) => {
                        const skillId = assignment.test?.skillId?.toString() ?? "";
                        let color = "gray";
                        let matchedSkill: Skill | null = null;
                        const existSkill = applicationStore.skills.find(s => s.skillId == skillId) ?? null;

                        if (existSkill != null) {
                            if (assignment.assignmentStatusId == AssignmentStatus.Started) {
                                color = "orange";
                            } else if (assignment.assignmentStatusId == AssignmentStatus.Completed) {
                                // const rating = matchedSkill.confirmedLevel || 0;
                                // color = rating > 4 ? "green" : "red";
                                color = "green";
                            }
                            else if (assignment.assignmentStatusId == AssignmentStatus.Expired) {
                                color = "red";
                            }
                        }

                        if (matchedSkill == null && skillId != null) {
                            const findOtherSkill = applicationStore.skills.find(s => s.skillId == skillId.toString()) ?? null;
                            matchedSkill = toJS(findOtherSkill);
                        }

                        if (!matchedSkill) {
                            return null;
                        }

                        return { ...matchedSkill, color, assignment };
                    })
                    .filter(Boolean);

                return (
                    <Flex wrap style={{ maxWidth: 200 }} gap={10}>
                        {(expandedSkills[project.id] ? relevantSkills : relevantSkills.slice(0, 5)).map((skill: any, index) => (
                            <Tooltip
                                key={`${skill.skillId}-${index}`}
                                title={
                                    <div>
                                        <div><strong>{skill.assignment.test?.name || skill.title}</strong></div>
                                        <div>{t("Level")}: {skill.assignment.test?.level || t("Unknown")}</div>
                                        <div>{t("tests.Result")}: {Math.max(...skill.assignment.testAttempts.map((t: TestAttempt) => t.percentage)) > 80
                                            ? t("passage_test.test_passed")
                                            : t("passage_test.test_failed")}
                                        </div>
                                        <div>
                                            {t("Finish date")}: {dayjs(skill.assignment.finishAssignedDate)
                                                .locale('uk')
                                                .format('DD.MM.YYYY HH:mm') || t("N/A")}
                                        </div>
                                        <div>{t("Status")}: {t(skill.assignment.assignmentStatus?.name ?? "")}</div>
                                    </div>
                                }
                            >
                                <Tag color={skill.color}>{skill.title}</Tag>
                            </Tooltip>
                        ))}
                        {relevantSkills?.length > 5 && (
                            expandedSkills[project.id] ? (
                                <Tag
                                    onClick={() => setExpandedSkills(prevState => ({
                                        ...prevState,
                                        [project.id]: false
                                    }))}
                                    style={{ cursor: 'pointer' }}
                                    color="blue"
                                >
                                    {t("ShowLess")}
                                </Tag>
                            ) : (
                                <Tag
                                    onClick={() => setExpandedSkills(prevState => ({
                                        ...prevState,
                                        [project.id]: true
                                    }))}
                                    style={{ cursor: 'pointer' }}
                                    color="blue"
                                >
                                    +{relevantSkills.length - 5} {t("more")}
                                </Tag>
                            )
                        )}
                        {relevantSkills?.length === 0 && (
                            <div>{t("tests.no_tests_assigment")}</div>
                        )}
                    </Flex>
                );
            },
            sorter: (a: Employee, b: Employee) => TextSort(a.firstName, b.firstName),
            filters: applicationStore.skills?.map(skill => { return { text: skill.title, value: skill.skillId } }),
            onFilter: (value, record) => record.userSkills.some(el => el.skill.skillId === value),
        },
    ];

    const columnsAttemptTable = [
        {
            title: t("tests.attemptNumber"),
            dataIndex: "numberAttempt",
            key: "numberAttempt",
        },
        {
            title: t("tests.testName"),
            dataIndex: "testId",
            key: "testId",
            render: (testId: number) => {
                var existTest = testsStore.testsPublic.find(t => t.id == testId);

                if (!existTest) {
                    existTest = testsStore.testsCompany.find(t => t.id == testId);
                }

                return existTest ? `${applicationStore.skills.find(s => s.skillId == existTest?.skillId?.toString())?.title} - ${existTest.level}` : t("tests.no_test_name_findt");
            },
        },
        {
            title: t("tests.grade"),
            dataIndex: "percentage",
            key: "percentage",
            render: (value: number) => `${value?.toFixed(2) || 0}/100%`,
        },
        {
            title: t("Start Date"),
            dataIndex: "startDate",
            key: "startDate",
            render: (value: string) =>
                value
                    ? new Date(value).toLocaleString("uk-UA", {
                        day: "numeric",
                        month: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                    }).replace(/,/g, "")
                    : t("No date"),
        },
        {
            title: t("tests.finishDate"),
            dataIndex: "finishDate",
            key: "finishDate",
            render: (value: string) =>
                value
                    ? new Date(value).toLocaleString("uk-UA", {
                        day: "numeric",
                        month: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                    }).replace(/,/g, "")
                    : t("No date"),
        },
        {
            title: `${t("tests.timeSpent")} (${t("passage_test.minutes")})`,
            dataIndex: "timeSpentMinutes",
            key: "timeSpentMinutes",
        },
        ...(applicationStore.user?.roles?.some(r => (r.id === Roles.TestManager || r.id === Roles.Owner || r.id === Roles.Admin))
            ? [
                {
                    title: t("Actions"),
                    key: "actions",
                    render: (record: TestAttempt) => (
                        <a
                            onClick={() => navigate(`/${applicationStore.company.Tenant}/tests/result/${record.id}`)}
                        >
                            <EyeOutlined />
                        </a>
                    ),
                },
            ]
            : []),
    ];

    return (
        <Spin spinning={loading}>
            {applicationStore.isFreezeModalVisible ? (
                <p style={{ padding: '20px', textAlign: 'center' }}>Access to users is limited until the subscription is renewed.</p>
            ) : (
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={filteredUser.filter(user => user.isActive !== false)}
                    expandable={{
                        expandedRowKeys: expandedRowKey,
                        onExpand: onExpandHandle,
                        expandIcon: ({ expanded, onExpand, record }: any) => (
                            <Button
                                shape="circle"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onExpand(record, e);
                                }}
                            >
                                {expanded ? <UpOutlined /> : <DownOutlined />}
                            </Button>
                        ),
                        expandRowByClick: true,
                        expandedRowRender: (record) => {
                            const assignedTests = userAssignments.filter(
                                (ua) => ua.assignedToUserId === Number(record.id)
                            );

                            const paginatedTests = assignedTests.slice((currentPage - 1) * pageSize, currentPage * pageSize);

                            return (
                                <Tabs defaultActiveKey="1" style={{ width: "100%" }}>
                                    <TabPane tab={t(`tests.myAssignments`)} key={1}>
                                        <Table
                                            columns={columnsAssignment}
                                            dataSource={paginatedTests}
                                            rowKey="id"
                                            pagination={false}
                                            style={{ width: "100%" }}
                                        />
                                        <Pagination
                                            current={currentPage}
                                            pageSize={pageSize}
                                            total={assignedTests.length}
                                            showSizeChanger
                                            pageSizeOptions={["5", "10", "20", "50"]}
                                            onChange={handleChangePage}
                                            style={{ marginTop: "15px", textAlign: "center" }}
                                        />
                                    </TabPane>
                                    <TabPane tab={t(`tests.myAttempts`)} key={2} style={{ width: "100%" }}>
                                        <Table
                                            dataSource={allUserAttempt}
                                            rowKey="id"
                                            pagination={{
                                                current: currentPageAttempt,
                                                pageSize: pageSizeAttemptTable,
                                                total: allUserAttempt.length,
                                                onChange: handleChangePageAttempt,
                                                position: ["bottomLeft"],
                                            }}
                                            columns={columnsAttemptTable}
                                        />
                                    </TabPane>
                                </Tabs>
                            );
                        }
                    }}
                />
            )}

            <Modal
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={[
                    <Button key="close" onClick={handleModalClose}>
                        {t("tests.Close")}
                    </Button>,
                ]}
                width={800}
                style={{ maxWidth: "90%", top: 20 }}
            >
                {selectedAttempts.length > 0 ? (
                    <Table
                        dataSource={selectedAttempts}
                        rowKey="id"
                        pagination={false}
                        columns={columnsAttemptTable}
                    />
                ) : (
                    <p>{t("tests.No attempts")}</p>
                )}
            </Modal>
        </Spin>
    )
});